import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Modal,
  Row,
  Space,
  Spin,
  Tag,
  Typography,
} from "antd";
import {
  CalendarOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import {
  GoogleMap,
  Marker,
  Polyline,
} from "@react-google-maps/api";
import { useDispatch, useSelector } from "react-redux";

import { fetchMyRouteSchedule } from "../workRoutesThunk";

const { RangePicker } = DatePicker;
const { Text, Title } = Typography;

const MyRouteSchedule = () => {
  const dispatch = useDispatch();

  const {
    myRouteSchedule = [],
    myRouteScheduleLoading,
  } = useSelector((state) => state.workRoutes);

  const [dateRange, setDateRange] = useState([dayjs(), dayjs().add(6, "day")]);
  const [routeMapOpen, setRouteMapOpen] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [selectedRouteDate, setSelectedRouteDate] = useState(null);
  const [routeMap, setRouteMap] = useState(null);

  const loadSchedule = (range = dateRange) => {
    if (!range?.[0] || !range?.[1]) return;

    dispatch(
      fetchMyRouteSchedule({
        from_date: range[0].format("YYYY-MM-DD"),
        to_date: range[1].format("YYYY-MM-DD"),
      })
    );
  };

  useEffect(() => {
    loadSchedule();
  }, []);

  useEffect(() => {
    if (
        !routeMapOpen ||
        !routeMap ||
        !selectedRoute?.points?.length ||
        !window.google
    ) {
        return;
    }

    const bounds = new window.google.maps.LatLngBounds();

    selectedRoute.points.forEach((point) => {
        bounds.extend({
        lat: Number(point.latitude),
        lng: Number(point.longitude),
        });
    });

    routeMap.fitBounds(bounds);

    // Avoid excessive zoom when route contains only one point
    if (selectedRoute.points.length === 1) {
        const point = selectedRoute.points[0];

        routeMap.setCenter({
        lat: Number(point.latitude),
        lng: Number(point.longitude),
        });

        routeMap.setZoom(15);
    }
    }, [routeMapOpen, routeMap, selectedRoute]);
    

  const handleDateChange = (dates) => {
    if (!dates) return;

    setDateRange(dates);
  };

  const handleApply = () => {
    loadSchedule();
  };

  const openFullRouteMap = (route, date) => {
    const validPoints = (route.points || []).filter(
        (point) =>
        point.latitude != null &&
        point.longitude != null &&
        !Number.isNaN(Number(point.latitude)) &&
        !Number.isNaN(Number(point.longitude))
    );

    setSelectedRoute({
        ...route,
        points: validPoints,
    });

    setSelectedRouteDate(date);
    setRouteMapOpen(true);
    };

  const closeFullRouteMap = () => {
    setRouteMapOpen(false);
    setSelectedRoute(null);
    setSelectedRouteDate(null);
    setRouteMap(null);
    };

  const openMap = (point) => {
    if (!point.latitude || !point.longitude) return;

    const url = `https://www.google.com/maps/search/?api=1&query=${point.latitude},${point.longitude}`;

    window.open(url, "_blank", "noopener,noreferrer");
  };

  const totalRoutes = myRouteSchedule.reduce(
    (sum, day) => sum + (day.routes?.length || 0),
    0
  );

  const totalVisits = myRouteSchedule.reduce(
    (sum, day) =>
      sum +
      (day.routes || []).reduce(
        (routeSum, route) =>
          routeSum + (route.points?.length || 0),
        0
      ),
    0
  );

  return (
    <div style={{ padding: 8 }}>
      <Card>
        <Row
          gutter={[12, 12]}
          justify="space-between"
          align="middle"
        >
          <Col xs={24} md={12}>
            <Title level={4} style={{ margin: 0 }}>
              My Visit Schedule
            </Title>

            <Text type="secondary">
              View your upcoming assigned routes and visiting
              locations.
            </Text>
          </Col>

          <Col xs={24} md={12}>
            <Space
              wrap
              style={{
                width: "100%",
                justifyContent: "flex-end",
              }}
            >
              <RangePicker
                value={dateRange}
                onChange={handleDateChange}
                allowClear={false}
                format="DD-MM-YYYY"
              />

              <Button
                type="primary"
                onClick={handleApply}
                icon={<CalendarOutlined />}
              >
                Apply
              </Button>
            </Space>
          </Col>
        </Row>

        <Row gutter={[12, 12]} style={{ marginTop: 16 }}>
          <Col xs={12} sm={8} md={6}>
            <Card size="small">
              <Text type="secondary">Scheduled Days</Text>

              <div style={{ fontSize: 22, fontWeight: 600 }}>
                {myRouteSchedule.length}
              </div>
            </Card>
          </Col>

          <Col xs={12} sm={8} md={6}>
            <Card size="small">
              <Text type="secondary">Routes</Text>

              <div style={{ fontSize: 22, fontWeight: 600 }}>
                {totalRoutes}
              </div>
            </Card>
          </Col>

          <Col xs={12} sm={8} md={6}>
            <Card size="small">
              <Text type="secondary">
                Visiting Locations
              </Text>

              <div style={{ fontSize: 22, fontWeight: 600 }}>
                {totalVisits}
              </div>
            </Card>
          </Col>
        </Row>
      </Card>

      <Spin spinning={myRouteScheduleLoading}>
        <div style={{ marginTop: 16 }}>
          {!myRouteSchedule.length &&
          !myRouteScheduleLoading ? (
            <Card>
              <Empty
                description="No scheduled visits found for the selected date range."
              />
            </Card>
          ) : (
            myRouteSchedule.map((day) => (
              <Card
                key={day.date}
                style={{ marginBottom: 16 }}
                title={
                  <Space wrap>
                    <CalendarOutlined />

                    <Text strong>
                      {dayjs(day.date)
                        .format("dddd, DD MMMM YYYY")
                        .toUpperCase()}
                    </Text>

                    {dayjs(day.date).isSame(dayjs(), "day") && (
                      <Tag color="blue">Today</Tag>
                    )}
                  </Space>
                }
              >
                {(day.routes || []).map((route) => (
                  <Card
                    key={`${day.date}-${route.assignment_id}`}
                    type="inner"
                    style={{ marginBottom: 12 }}
                    title={
                        <div>
                        <Text strong>
                            {route.route_name}
                        </Text>

                        <div>
                            <Text type="secondary">
                            {route.points?.length || 0} visiting locations
                            </Text>
                        </div>
                        </div>
                    }
                    extra={
                        <Button
                        size="small"
                        icon={<EnvironmentOutlined />}
                        onClick={() =>
                            openFullRouteMap(route, day.date)
                        }
                        disabled={
                            !(route.points || []).some(
                            (point) =>
                                point.latitude != null &&
                                point.longitude != null
                            )
                        }
                        >
                        View Full Route Map
                        </Button>
                    }
                    >
                    {(route.points || []).map(
                      (point, index) => (
                        <div
                          key={point.route_point_id}
                          style={{
                            display: "flex",
                            gap: 12,
                            padding: "12px 0",
                            borderBottom:
                              index !== route.points.length - 1
                                ? "1px solid #f0f0f0"
                                : "none",
                          }}
                        >
                          <div
                            style={{
                              minWidth: 30,
                              height: 30,
                              borderRadius: "50%",
                              border: "1px solid #d9d9d9",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontWeight: 600,
                            }}
                          >
                            {point.order || index + 1}
                          </div>

                          <div style={{ flex: 1 }}>
                            <Space wrap>
                              <Text strong>
                                {point.name}
                              </Text>

                              {point.related_type && (
                                <Tag>
                                  {point.related_type
                                    .charAt(0)
                                    .toUpperCase() +
                                    point.related_type.slice(1)}
                                </Tag>
                              )}
                            </Space>

                            {point.customer_name && (
                              <div style={{ marginTop: 4 }}>
                                <Text type="secondary">
                                  Customer:{" "}
                                  {point.customer_name}

                                  {point.customer_code
                                    ? ` (${point.customer_code})`
                                    : ""}
                                </Text>
                              </div>
                            )}

                            <div style={{ marginTop: 6 }}>
                              <EnvironmentOutlined
                                style={{ marginRight: 6 }}
                              />

                              <Text>
                                {point.address ||
                                  "Address not available"}
                              </Text>
                            </div>
                          </div>

                          <div>
                            <Button
                              size="small"
                              icon={<EnvironmentOutlined />}
                              disabled={
                                !point.latitude ||
                                !point.longitude
                              }
                              onClick={() => openMap(point)}
                            >
                              View Map
                            </Button>
                          </div>
                        </div>
                      )
                    )}
                  </Card>
                ))}
              </Card>
            ))
          )}
        </div>
      </Spin>
      <Modal
            open={routeMapOpen}
            title={
                selectedRoute ? (
                <div>
                    <div>
                    {selectedRoute.route_name}
                    </div>

                    {selectedRouteDate && (
                    <Text
                        type="secondary"
                        style={{ fontSize: 13 }}
                    >
                        {dayjs(selectedRouteDate).format(
                        "dddd, DD MMMM YYYY"
                        )}
                    </Text>
                    )}
                </div>
                ) : (
                "Route Map"
                )
            }
            onCancel={closeFullRouteMap}
            footer={null}
            width={1000}
            destroyOnClose
            >
            {selectedRoute?.points?.length ? (
                <>
                <GoogleMap
                    mapContainerStyle={{
                    width: "100%",
                    height: "500px",
                    borderRadius: 8,
                    }}
                    center={{
                    lat: Number(
                        selectedRoute.points[0].latitude
                    ),
                    lng: Number(
                        selectedRoute.points[0].longitude
                    ),
                    }}
                    zoom={11}
                    onLoad={(mapInstance) =>
                    setRouteMap(mapInstance)
                    }
                    onUnmount={() => setRouteMap(null)}
                >
                    {selectedRoute.points.map(
                    (point, index) => (
                        <Marker
                        key={point.route_point_id}
                        position={{
                            lat: Number(point.latitude),
                            lng: Number(point.longitude),
                        }}
                        title={
                            point.customer_name
                            ? `${index + 1}. ${point.name} - ${point.customer_name}`
                            : `${index + 1}. ${point.name}`
                        }
                        label={{
                            text: String(
                            point.order || index + 1
                            ),
                            color: "#ffffff",
                            fontWeight: "bold",
                        }}
                        />
                    )
                    )}

                    {selectedRoute.points.length > 1 && (
                    <Polyline
                        path={selectedRoute.points.map(
                        (point) => ({
                            lat: Number(point.latitude),
                            lng: Number(point.longitude),
                        })
                        )}
                        options={{
                        strokeOpacity: 0.8,
                        strokeWeight: 4,
                        }}
                    />
                    )}
                </GoogleMap>

                <div
                    style={{
                    marginTop: 16,
                    maxHeight: 220,
                    overflowY: "auto",
                    }}
                >
                    {selectedRoute.points.map(
                    (point, index) => (
                        <div
                        key={point.route_point_id}
                        style={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 10,
                            padding: "10px 0",
                            borderBottom:
                            index !==
                            selectedRoute.points.length - 1
                                ? "1px solid #f0f0f0"
                                : "none",
                        }}
                        >
                        <div
                            style={{
                            minWidth: 28,
                            height: 28,
                            borderRadius: "50%",
                            border: "1px solid #d9d9d9",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: 600,
                            }}
                        >
                            {point.order || index + 1}
                        </div>

                        <div style={{ flex: 1 }}>
                            <Space wrap>
                            <Text strong>
                                {point.name}
                            </Text>

                            {point.related_type && (
                                <Tag>
                                {point.related_type
                                    .charAt(0)
                                    .toUpperCase() +
                                    point.related_type.slice(1)}
                                </Tag>
                            )}
                            </Space>

                            {point.customer_name && (
                            <div>
                                <Text type="secondary">
                                Customer:{" "}
                                {point.customer_name}
                                </Text>
                            </div>
                            )}

                            <div>
                            <Text type="secondary">
                                {point.address ||
                                "Address not available"}
                            </Text>
                            </div>
                        </div>

                        <Button
                            size="small"
                            icon={<EnvironmentOutlined />}
                            onClick={() => openMap(point)}
                        >
                            Open in Maps
                        </Button>
                        </div>
                    )
                    )}
                </div>
                </>
            ) : (
                <Empty description="No mapped locations available for this route." />
            )}
            </Modal>
    </div>
  );
};

export default MyRouteSchedule;